import React, {useState} from 'react';
import {motion, AnimatePresence} from 'framer-motion';
import * as XLSX from 'xlsx';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../lib/supabase';

const {FiUpload, FiX, FiCheck, FiAlertTriangle, FiDownload} = FiIcons;

const ExcelUploader = ({isOpen, onClose, onSuccess}) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({current: 0, total: 0, percentage: 0});

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.match(/\.(xlsx|xls|csv)$/)) {
      setError('Please select a valid Excel or CSV file');
      return;
    }

    setFile(selectedFile);
    setError(null);
    previewFile(selectedFile);
  };

  const previewFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, {type: 'array'});
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet, {header: 1});

        // Show first 5 rows for preview
        setPreview({
          headers: jsonData[0] || [],
          rows: jsonData.slice(1, 6) || [],
          totalRows: jsonData.length - 1
        });
      } catch (err) {
        setError('Error reading file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Ultra-optimized data transformation with memory management and FIXED discount calculation
  const transformDataUltraSafe = async (rawData) => {
    return new Promise((resolve, reject) => {
      try {
        const headers = rawData[0];
        const rows = rawData.slice(1);
        const transformedRows = [];

        // Process in extremely small chunks with async breaks
        const MICRO_CHUNK_SIZE = 50; // Very small chunks
        let currentIndex = 0;

        const processChunk = async () => {
          try {
            const endIndex = Math.min(currentIndex + MICRO_CHUNK_SIZE, rows.length);
            const chunk = rows.slice(currentIndex, endIndex);

            for (let i = 0; i < chunk.length; i++) {
              const row = chunk[i];

              // Skip completely empty rows
              if (!row || row.length === 0 || row.every(cell => !cell)) {
                continue;
              }

              const obj = {};
              for (let j = 0; j < headers.length; j++) {
                const header = headers[j];
                if (header) {
                  const key = header.toString().toLowerCase().replace(/[^a-z0-9]/g, '_');
                  obj[key] = row[j] || null;
                }
              }

              // FIXED: Simple, safe parsing functions that preserve negative values
              const safeParseFloat = (value) => {
                if (value === null || value === undefined || value === '') return 0;
                const str = value.toString().replace(/[^0-9.-]/g, '');
                const parsed = parseFloat(str);
                return isNaN(parsed) ? 0 : parsed; // REMOVED Math.max(0, parsed) to allow negatives
              };

              const safeParseInt = (value) => {
                if (value === null || value === undefined || value === '') return 0;
                const str = value.toString().replace(/[^0-9-]/g, ''); // Allow negative sign
                const parsed = parseInt(str);
                return isNaN(parsed) ? 0 : parsed; // REMOVED Math.max(0, parsed) to allow negatives
              };

              // Extract and validate data - ALLOW NEGATIVE VALUES
              const grossSales = safeParseFloat(obj.gross_sales || obj.sales || obj.revenue);
              const netSales = safeParseFloat(obj.net_sales || obj.net);
              const units = safeParseInt(obj.units || obj.quantity || obj.qty);
              const cogs = safeParseFloat(obj.cogs || obj.cost);
              const profit = safeParseFloat(obj.profit || obj.gp || (netSales - cogs));
              const marginPct = netSales !== 0 ? (profit / netSales) * 100 : 0; // Allow negative margins

              // FIXED: Calculate discount using the formula: discount_amt = gross_sales - net_sales
              // ALLOW NEGATIVE DISCOUNTS (which could represent surcharges or price increases)
              let calculatedDiscount = 0;
              if (grossSales !== 0 || netSales !== 0) {
                calculatedDiscount = grossSales - netSales; // Can be negative
              }

              console.log(`Row ${i + 1} discount calculation: gross=${grossSales}, net=${netSales}, discount=${calculatedDiscount}`);

              // Handle date - simplified approach
              let txnDate = obj.date || obj.txn_date || obj.transaction_date;
              if (txnDate) {
                if (typeof txnDate === 'number' && txnDate > 40000 && txnDate < 50000) {
                  // Excel date serial number
                  const excelEpoch = new Date(1900, 0, 1);
                  const jsDate = new Date(excelEpoch.getTime() + (txnDate - 2) * 24 * 60 * 60 * 1000);
                  txnDate = jsDate.toISOString().split('T')[0];
                } else {
                  // Try to parse as string
                  const parsed = new Date(txnDate);
                  if (!isNaN(parsed.getTime())) {
                    txnDate = parsed.toISOString().split('T')[0];
                  } else {
                    txnDate = new Date().toISOString().split('T')[0]; // Fallback to today
                  }
                }
              } else {
                txnDate = new Date().toISOString().split('T')[0]; // Fallback to today
              }

              // Include rows with minimum required data - ALLOW NEGATIVE VALUES
              const productName = obj.product || obj.product_name || obj.name;
              if (productName) { // Only require product name, allow negative values
                transformedRows.push({
                  txn_date: txnDate,
                  product_name: productName.toString().substring(0, 255), // Limit length
                  category: (obj.category || 'Uncategorized').toString().substring(0, 100),
                  department: (obj.department || 'General').toString().substring(0, 100),
                  gross_sales: Math.round(grossSales * 100) / 100, // Round to 2 decimals, allow negative
                  discount_amt: Math.round(calculatedDiscount * 100) / 100, // FIXED: Use calculated discount, allow negative
                  net_sales: Math.round(netSales * 100) / 100, // Allow negative
                  units: units, // Allow negative (returns/refunds)
                  margin_pct: Math.round(marginPct * 100) / 100, // Allow negative margins
                  profit: Math.round(profit * 100) / 100, // Allow negative profit (losses)
                  cogs: Math.round(cogs * 100) / 100, // Allow negative COGS if needed
                  created_at: new Date().toISOString()
                });
              }
            }

            currentIndex = endIndex;

            // Continue processing if there are more rows
            if (currentIndex < rows.length) {
              // Use setTimeout to prevent stack overflow
              setTimeout(processChunk, 10);
            } else {
              // Processing complete
              console.log(`✅ Discount calculation complete. Processed ${transformedRows.length} valid records with calculated discounts (including negative values).`);
              resolve(transformedRows);
            }
          } catch (chunkError) {
            reject(new Error(`Chunk processing failed: ${chunkError.message}`));
          }
        };

        // Start processing
        processChunk();
      } catch (error) {
        reject(error);
      }
    });
  };

  // Ultra-safe batch upload with extensive error handling
  const uploadDataUltraSafe = async (transformedData) => {
    const ULTRA_BATCH_SIZE = 25; // Even smaller batches
    const totalBatches = Math.ceil(transformedData.length / ULTRA_BATCH_SIZE);
    let totalInserted = 0;

    setUploadProgress({current: 0, total: totalBatches, percentage: 0});

    for (let batchIndex = 0; batchIndex < totalBatches; batchIndex++) {
      const start = batchIndex * ULTRA_BATCH_SIZE;
      const end = Math.min(start + ULTRA_BATCH_SIZE, transformedData.length);
      const batch = transformedData.slice(start, end);

      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          const {data: insertedData, error: insertError} = await supabase
            .from('sales_data_br7x9m4k2q')
            .insert(batch);

          if (insertError) {
            throw insertError;
          }

          totalInserted += batch.length;
          break; // Success, exit retry loop
        } catch (error) {
          retryCount++;
          console.error(`Batch ${batchIndex + 1} attempt ${retryCount} failed:`, error);

          if (retryCount >= maxRetries) {
            throw new Error(`Batch ${batchIndex + 1} failed after ${maxRetries} attempts: ${error.message}`);
          }

          // Wait before retry
          await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
        }
      }

      // Update progress
      const progress = {
        current: batchIndex + 1,
        total: totalBatches,
        percentage: Math.round(((batchIndex + 1) / totalBatches) * 100)
      };
      setUploadProgress(progress);

      // Longer delay between batches to prevent overwhelming
      if (batchIndex < totalBatches - 1) {
        await new Promise(resolve => setTimeout(resolve, 200));
      }
    }

    return totalInserted;
  };

  const uploadData = async () => {
    if (!file) return;

    setUploading(true);
    setError(null);
    setUploadProgress({current: 0, total: 0, percentage: 0});

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          console.log('Reading file...');
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, {type: 'array'});
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];

          // Use streaming approach for very large files
          const rawData = XLSX.utils.sheet_to_json(firstSheet, {
            header: 1,
            raw: false, // Convert everything to strings first
            defval: '' // Default value for empty cells
          });

          console.log(`File contains ${rawData.length - 1} rows`);

          if (rawData.length > 10000) {
            throw new Error(`File too large (${rawData.length - 1} rows). Please split into files under 10,000 rows each.`);
          }

          // Transform data with ultra-safe processing and FIXED discount calculation
          console.log('Starting ultra-safe transformation with automatic discount calculation (allowing negative values)...');
          const transformedData = await transformDataUltraSafe(rawData);

          if (transformedData.length === 0) {
            throw new Error('No valid data found. Please check your file format and ensure required columns are present.');
          }

          console.log(`Transformed ${transformedData.length} valid records with calculated discounts (including negative values)`);

          // Upload with ultra-safe batching
          console.log('Starting ultra-safe upload...');
          const totalInserted = await uploadDataUltraSafe(transformedData);

          console.log(`Successfully inserted ${totalInserted} records with calculated discounts (including negative values)`);

          setSuccess(true);
          setTimeout(() => {
            onSuccess();
            onClose();
          }, 2000);
        } catch (err) {
          console.error('Upload processing error:', err);
          setError(err.message || 'Upload failed');
        } finally {
          setUploading(false);
          setUploadProgress({current: 0, total: 0, percentage: 0});
        }
      };

      reader.onerror = () => {
        setError('Failed to read file');
        setUploading(false);
      };

      reader.readAsArrayBuffer(file);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err.message || 'Upload failed');
      setUploading(false);
      setUploadProgress({current: 0, total: 0, percentage: 0});
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      ['date', 'product_name', 'category', 'department', 'gross_sales', 'net_sales', 'units', 'margin_pct', 'profit', 'cogs'],
      ['2025-01-15', 'Classic Brioche', 'Bakery', 'Pastries', 1250.00, 1200.00, 25, 38.5, 462.00, 738.00],
      ['2025-01-15', 'Chocolate Brioche', 'Bakery', 'Pastries', 980.00, 950.00, 18, 42.1, 400.00, 550.00]
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sales Data Template');
    XLSX.writeFile(wb, 'sales_data_template.xlsx');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          onClick={onClose}
        >
          <motion.div
            className="bg-brand-card rounded-card p-6 w-full max-w-2xl mx-4 shadow-card max-h-[90vh] overflow-y-auto"
            initial={{scale: 0.9, opacity: 0}}
            animate={{scale: 1, opacity: 1}}
            exit={{scale: 0.9, opacity: 0}}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-brand-fg">Upload Sales Data</h3>
              <motion.button
                onClick={onClose}
                disabled={uploading}
                className="p-2 hover:bg-brand-muted2/20 rounded-button transition-colors disabled:opacity-50"
                whileHover={{scale: 1.05}}
                whileTap={{scale: 0.95}}
              >
                <SafeIcon icon={FiX} className="w-5 h-5 text-brand-muted" />
              </motion.button>
            </div>

            {success ? (
              <motion.div
                className="text-center py-8"
                initial={{opacity: 0, scale: 0.8}}
                animate={{opacity: 1, scale: 1}}
              >
                <SafeIcon icon={FiCheck} className="w-16 h-16 text-brand-good mx-auto mb-4" />
                <h4 className="text-lg font-semibold text-brand-fg mb-2">Upload Successful!</h4>
                <p className="text-brand-muted">Your sales data has been imported with calculated discounts (including negative values).</p>
              </motion.div>
            ) : (
              <>
                {/* Enhanced File Size Warning */}
                <div className="mb-6 p-4 bg-brand-accent/10 rounded-lg border border-brand-accent/20">
                  <h4 className="text-brand-accent font-medium mb-2">🚀 Ultra-Robust Upload System</h4>
                  <ul className="text-brand-muted text-sm space-y-1">
                    <li>• <strong>Maximum file size:</strong> 10,000 rows (recommended under 5,000)</li>
                    <li>• <strong>Processing:</strong> Micro-chunks with memory management</li>
                    <li>• <strong>Upload:</strong> 25-record batches with retry logic</li>
                    <li>• <strong>Discount calculation:</strong> Automatic (gross_sales - net_sales, allows negative)</li>
                    <li>• <strong>Values:</strong> Preserves negative amounts (losses, refunds, surcharges)</li>
                    <li>• <strong>Large files:</strong> Please split into smaller files</li>
                  </ul>
                </div>

                {/* Template Download */}
                <div className="mb-6 p-4 bg-brand-good/10 rounded-lg border border-brand-good/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-brand-fg font-medium mb-1">Need a template?</h4>
                      <p className="text-brand-muted text-sm">Download our Excel template with the correct column format</p>
                      <p className="text-brand-muted text-xs mt-1">Note: Discounts are automatically calculated from gross_sales - net_sales (allows negative values)</p>
                    </div>
                    <motion.button
                      onClick={downloadTemplate}
                      className="flex items-center space-x-2 bg-brand-good hover:bg-brand-good/80 text-brand-bg px-4 py-2 rounded-button text-sm"
                      whileHover={{scale: 1.02}}
                      whileTap={{scale: 0.98}}
                    >
                      <SafeIcon icon={FiDownload} className="w-4 h-4" />
                      <span>Download Template</span>
                    </motion.button>
                  </div>
                </div>

                {/* File Upload */}
                <div className="mb-6">
                  <label className="block text-brand-fg text-sm font-medium mb-3">
                    Select Excel or CSV File
                  </label>
                  <div className="border-2 border-dashed border-brand-muted2/40 rounded-lg p-8 text-center hover:border-brand-accent/50 transition-colors">
                    <SafeIcon icon={FiUpload} className="w-12 h-12 text-brand-muted mx-auto mb-4" />
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileChange}
                      disabled={uploading}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className={`cursor-pointer text-brand-fg hover:text-brand-accent transition-colors ${uploading ? 'pointer-events-none opacity-50' : ''}`}
                    >
                      Click to upload or drag and drop
                    </label>
                    <p className="text-brand-muted text-sm mt-2">
                      Maximum 10,000 rows • Supports .xlsx, .xls, and .csv files
                    </p>
                    <p className="text-brand-muted text-xs mt-1">
                      Discounts will be calculated automatically from gross_sales and net_sales (preserves negative values)
                    </p>
                  </div>
                </div>

                {/* Upload Progress */}
                {uploading && uploadProgress.total > 0 && (
                  <motion.div
                    className="mb-6 p-4 bg-brand-accent/10 rounded-lg border border-brand-accent/20"
                    initial={{opacity: 0, y: -10}}
                    animate={{opacity: 1, y: 0}}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-brand-fg font-medium">Uploading Data...</span>
                      <span className="text-brand-accent font-semibold">{uploadProgress.percentage}%</span>
                    </div>
                    <div className="w-full bg-brand-muted2/20 rounded-full h-2 mb-2">
                      <motion.div
                        className="bg-brand-accent h-2 rounded-full transition-all duration-300"
                        initial={{width: 0}}
                        animate={{width: `${uploadProgress.percentage}%`}}
                      />
                    </div>
                    <p className="text-brand-muted text-sm">
                      Batch {uploadProgress.current} of {uploadProgress.total} • Ultra-safe processing with calculated discounts (preserving negative values)
                    </p>
                  </motion.div>
                )}

                {/* Enhanced Error Display */}
                {error && (
                  <motion.div
                    className="mb-6 p-4 bg-brand-bad/10 border border-brand-bad/20 rounded-lg"
                    initial={{opacity: 0, y: -10}}
                    animate={{opacity: 1, y: 0}}
                  >
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-brand-bad" />
                      <span className="text-brand-bad font-medium">Upload Error</span>
                    </div>
                    <p className="text-brand-bad text-sm mt-1">{error}</p>
                    <div className="mt-3 p-3 bg-brand-bad/5 rounded border border-brand-bad/10">
                      <p className="text-brand-bad text-xs font-medium mb-2">💡 Troubleshooting Tips:</p>
                      <ul className="text-brand-bad text-xs space-y-1">
                        <li>• <strong>Large files:</strong> Split your file into chunks under 5,000 rows</li>
                        <li>• <strong>File format:</strong> Ensure columns match our template</li>
                        <li>• <strong>Required columns:</strong> product_name, gross_sales, net_sales</li>
                        <li>• <strong>Data quality:</strong> Remove completely empty rows</li>
                        <li>• <strong>Memory issues:</strong> Close other browser tabs and retry</li>
                        <li>• <strong>Still failing?</strong> Try uploading a smaller sample first</li>
                      </ul>
                    </div>
                  </motion.div>
                )}

                {/* Preview with Upload Button */}
                {preview && (
                  <motion.div
                    className="mb-6"
                    initial={{opacity: 0, y: 10}}
                    animate={{opacity: 1, y: 0}}
                  >
                    {/* File Size Warning */}
                    {preview.totalRows > 5000 && (
                      <div className="mb-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 text-orange-500" />
                          <span className="text-orange-500 font-medium text-sm">Large File Warning</span>
                        </div>
                        <p className="text-orange-500 text-xs mt-1">
                          Your file has {preview.totalRows} rows. For best results, consider splitting into smaller files.
                        </p>
                      </div>
                    )}

                    {/* Preview Header with Upload Button */}
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-brand-fg font-medium">
                        Preview ({preview.totalRows} rows total)
                      </h4>
                      <motion.button
                        onClick={uploadData}
                        disabled={!file || uploading || preview.totalRows > 10000}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-button text-sm transition-colors ${
                          !file || uploading || preview.totalRows > 10000
                            ? 'bg-brand-muted2/20 text-brand-muted cursor-not-allowed'
                            : 'bg-brand-accent hover:bg-brand-accent/80 text-brand-bg'
                        }`}
                        whileHover={!file || uploading || preview.totalRows > 10000 ? {} : {scale: 1.02}}
                        whileTap={!file || uploading || preview.totalRows > 10000 ? {} : {scale: 0.98}}
                      >
                        {uploading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-brand-muted border-t-transparent rounded-full animate-spin" />
                            <span>Processing...</span>
                          </>
                        ) : preview.totalRows > 10000 ? (
                          <>
                            <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" />
                            <span>File Too Large</span>
                          </>
                        ) : (
                          <>
                            <SafeIcon icon={FiUpload} className="w-4 h-4" />
                            <span>Upload with Auto-Discount</span>
                          </>
                        )}
                      </motion.button>
                    </div>

                    {/* Preview Table */}
                    <div className="bg-brand-muted2/10 rounded-lg p-4 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-brand-muted2/20">
                            {preview.headers.map((header, index) => (
                              <th key={index} className="text-left p-2 text-brand-muted font-medium">
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {preview.rows.map((row, rowIndex) => (
                            <tr key={rowIndex} className="border-b border-brand-muted2/10">
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex} className="p-2 text-brand-fg">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Discount Calculation Notice */}
                    <div className="mt-3 p-3 bg-brand-accent/10 rounded-lg border border-brand-accent/20">
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-brand-accent" />
                        <span className="text-brand-accent font-medium text-sm">Automatic Discount Calculation</span>
                      </div>
                      <p className="text-brand-muted text-xs mt-1">
                        Discounts will be calculated automatically using the formula: <strong>discount_amt = gross_sales - net_sales</strong>
                      </p>
                      <p className="text-brand-good text-xs mt-1">
                        ✅ <strong>Negative values preserved:</strong> Returns, refunds, losses, and surcharges will be shown accurately
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* Bottom Actions - Only Cancel when no preview */}
                {!preview && (
                  <div className="flex justify-end">
                    <motion.button
                      onClick={onClose}
                      disabled={uploading}
                      className="px-4 py-2 bg-brand-muted2/20 hover:bg-brand-muted2/30 text-brand-fg rounded-button transition-colors disabled:opacity-50"
                      whileHover={{scale: 1.02}}
                      whileTap={{scale: 0.98}}
                    >
                      Cancel
                    </motion.button>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExcelUploader;